import { useState, useRef, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Clock,
  CalendarDays,
  Send,
  CheckSquare,
  Palmtree,
  CalendarClock,
  Tag,
  Tags,
  FolderOpen,
  ListTodo,
  Building2,
  Users2,
  MessageSquare,
  Inbox,
  Pin,
  PinOff,
  GitBranch,
  Landmark,
  Bell,
  Receipt,
  BarChart2,
  MessageSquareText,
  Settings2,
} from "lucide-react";
import { useUserPref, PREF_HELP_AUTO_DISMISS } from "@/lib/userPrefs";

interface HelpSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export interface Topic {
  id: string;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

function Tip({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800 my-3">
      <span className="font-semibold">Tip: </span>{children}
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800 my-3">
      <span className="font-semibold">Note: </span>{children}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <h3 className="text-base font-semibold text-foreground mb-2 border-b pb-1">{title}</h3>
      <div className="text-sm text-muted-foreground space-y-2 leading-relaxed">{children}</div>
    </div>
  );
}

const LoggingTimeContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      Time entries are the foundation of everything in CommandHub. You can log time using the built-in
      timer or by entering hours manually.
    </p>

    <Section title="Using the Timer">
      <p>The timer lives in the top navigation bar. There are two ways to start it:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li>Click the <span className="font-medium text-foreground">▶ Play button</span> to start a timer immediately — a time entry sheet slides in from the right where you fill in the details.</li>
        <li>Click the <span className="font-medium text-foreground">Clock icon</span> to open the time entry sheet first, then start the timer from there.</li>
      </ul>
      <p className="mt-2">Once running, the clock button turns blue and shows the live elapsed time. You can navigate to any page — the timer keeps running in the background.</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Pause</span> — stops the clock but keeps your entry open (button turns amber).</li>
        <li><span className="font-medium text-foreground">Save</span> — stops the timer and saves the entry as a draft.</li>
        <li><span className="font-medium text-foreground">Discard</span> — cancels the entry and resets the timer.</li>
      </ul>
      <Tip>You can pause the timer, go do other things, and resume it later — even after navigating away. The elapsed time is saved locally so it survives page refreshes.</Tip>
    </Section>

    <Section title="Manual Entry">
      <p>Prefer to log time after the fact? Open the time entry sheet by clicking the Clock icon, skip the timer, and type a duration directly into the <span className="font-medium text-foreground">Duration</span> field.</p>
      <p className="mt-1">Duration accepts flexible formats:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-mono text-foreground">1.5</span> or <span className="font-mono text-foreground">1:30</span> → 1 hour 30 minutes</li>
        <li><span className="font-mono text-foreground">45</span> → 45 minutes</li>
        <li><span className="font-mono text-foreground">2</span> → 2 hours</li>
      </ul>
    </Section>

    <Section title="Required Fields">
      <p>Every time entry needs:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Client / Account</span> — the firm or individual you're working for.</li>
        <li><span className="font-medium text-foreground">Activity Code</span> — the type of work (e.g., Tax Prep, Review, Admin).</li>
        <li><span className="font-medium text-foreground">Duration</span> — how long you worked.</li>
        <li><span className="font-medium text-foreground">Date</span> — defaults to today, but can be changed.</li>
      </ul>
      <p className="mt-2">Optional but useful:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Project</span> — links the entry to a specific engagement or task.</li>
        <li><span className="font-medium text-foreground">Notes</span> — describe what you did. Useful for client billing and your own records.</li>
        <li><span className="font-medium text-foreground">Billable toggle</span> — mark whether this time is billable to the client.</li>
      </ul>
    </Section>

    <Section title="Editing Entries">
      <p>You can edit or delete any entry that is still in <span className="font-medium text-foreground">Draft</span> status. Go to the <span className="font-medium text-foreground">Today</span> page, hover over an entry, and click the edit (pencil) or delete (trash) icon.</p>
      <Note>Once you submit your timesheet, entries lock and cannot be edited unless a manager rejects them back to draft status.</Note>
    </Section>

    <Section title="The Today Page">
      <p>The <span className="font-medium text-foreground">Today</span> page (Time → Today in the sidebar) shows all your entries for a given day. Use the <span className="font-medium text-foreground">&lt; &gt;</span> arrows to navigate to other dates.</p>
      <p className="mt-1">A progress bar shows how many hours you've logged toward the daily 8-hour goal. Days with no entries are flagged in your timesheet view.</p>
    </Section>
  </div>
);

const TimesheetContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      The Timesheet page (Time → Timesheet) shows all your entries for a full pay period, grouped by day.
    </p>

    <Section title="Reading the Timesheet">
      <p>Each day in the pay period is shown as a row with a summary of hours logged. Expand a day to see individual entries with client, code, hours, and status.</p>
      <ul className="list-disc pl-5 space-y-1 mt-2">
        <li><span className="font-medium text-foreground">Green checkmark</span> — the day has entries logged.</li>
        <li><span className="font-medium text-red-600">Red warning</span> — it's a weekday with no entries. These are flagged so you don't miss them before submitting.</li>
        <li>Weekends are shown but not flagged for missing time.</li>
      </ul>
    </Section>

    <Section title="Navigating Pay Periods">
      <p>Use the <span className="font-medium text-foreground">&lt; &gt;</span> arrows in the header to switch between pay periods. The current period is labeled <span className="font-medium text-foreground">Current</span>. Past periods show their date range.</p>
      <p className="mt-1">You can only edit entries in the current period (unless a manager reopens a closed one).</p>
    </Section>

    <Section title="Summary Cards">
      <p>At the top of the timesheet, three cards show:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Total Hours</span> — all time logged in this period.</li>
        <li><span className="font-medium text-foreground">Billable Hours</span> — entries marked as billable.</li>
        <li><span className="font-medium text-foreground">PTO Hours</span> — PTO and holiday entries.</li>
      </ul>
    </Section>

    <Section title="Printing Your Timesheet">
      <p>Click the <span className="font-medium text-foreground">Print</span> button (top right of the timesheet page) to open a print-friendly layout. It includes signature lines for employee and manager — useful for record-keeping or review meetings.</p>
    </Section>
  </div>
);

const SubmittingContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      At the end of each pay period, you submit your time entries for manager review and approval. Submission is required before your time can be approved for payroll.
    </p>

    <Section title="When to Submit">
      <p>Submit your timesheet after you've logged all your time for the pay period. The firm's typical deadline is the last day of the period, but check with your manager for specific expectations.</p>
      <Tip>Review the Timesheet page first and resolve any flagged missing days before submitting.</Tip>
    </Section>

    <Section title="How to Submit">
      <ol className="list-decimal pl-5 space-y-2 mt-1">
        <li>Go to <span className="font-medium text-foreground">Time → Submit</span> in the sidebar.</li>
        <li>The page shows how many draft entries are pending and the total hours.</li>
        <li>Click <span className="font-medium text-foreground">Submit Time</span>.</li>
        <li>All draft entries for the current pay period move to <span className="font-medium text-foreground">Submitted</span> status.</li>
      </ol>
    </Section>

    <Section title="After Submitting">
      <p>Once submitted:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li>You <span className="font-medium text-foreground">cannot edit</span> your entries — they're locked pending review.</li>
        <li>Your manager will approve or reject them on the Approvals page.</li>
        <li>If an entry is <span className="font-medium text-red-600">rejected</span>, it returns to Draft status with a note explaining why. You can edit and resubmit.</li>
      </ul>
      <Note>If you submitted early but forgot an entry, ask your manager to reject the affected entry so you can add the missing time and resubmit.</Note>
    </Section>

    <Section title="Nothing to Submit?">
      <p>If the Submit page shows "All Caught Up!", it means all your entries for the current period are already submitted or approved. No action needed.</p>
    </Section>
  </div>
);

const ApprovalsContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      Managers and admins use the Approvals page (Time → Approvals) to review and approve submitted time entries from the team.
    </p>

    <Note>This page is only accessible to users with a Manager or Admin role. If you don't see it in the sidebar, you don't need it.</Note>

    <Section title="Reviewing Submitted Time">
      <p>The Approvals page lists all staff members who have submitted entries, along with their entry count and total hours. Click on a person to expand their individual entries.</p>
      <p className="mt-1">Each entry shows:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li>Date, client, activity code</li>
        <li>Duration and billable status</li>
        <li>Any notes the staff member added</li>
      </ul>
    </Section>

    <Section title="Approving Entries">
      <ol className="list-decimal pl-5 space-y-2 mt-1">
        <li>Check the box next to individual entries, or use <span className="font-medium text-foreground">Select All</span> to select everything for a staff member.</li>
        <li>Click <span className="font-medium text-foreground">Approve Selected</span>.</li>
        <li>Approved entries move to <span className="font-medium text-foreground">Approved</span> status and are locked for payroll.</li>
      </ol>
      <Tip>You can approve entries from multiple staff members in one pass — just check entries across the list before clicking Approve.</Tip>
    </Section>

    <Section title="Rejecting Entries">
      <ol className="list-decimal pl-5 space-y-2 mt-1">
        <li>Select the entry or entries to reject.</li>
        <li>Click <span className="font-medium text-foreground">Reject Selected</span>.</li>
        <li>A dialog prompts you for a rejection reason — this note is visible to the staff member.</li>
        <li>Rejected entries return to <span className="font-medium text-foreground">Draft</span> status so the staff member can correct and resubmit.</li>
      </ol>
    </Section>

    <Section title="Payroll Reports">
      <p>Once entries are approved, they appear in the Payroll Report (Reports section). Run the report after completing approvals for each pay period to export hours for processing.</p>
    </Section>
  </div>
);

const PTOContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      PTO (Paid Time Off) and holidays are logged as regular time entries using special activity codes — there's no separate PTO request form.
    </p>

    <Section title="Logging PTO">
      <ol className="list-decimal pl-5 space-y-2 mt-1">
        <li>Open the time entry sheet (click the Clock icon in the header, or the ▶ Play button).</li>
        <li>Set the <span className="font-medium text-foreground">Date</span> to the day you took off.</li>
        <li>In <span className="font-medium text-foreground">Activity Code</span>, select <span className="font-medium text-foreground">PTO</span> or <span className="font-medium text-foreground">Holiday</span> (whichever applies).</li>
        <li>For <span className="font-medium text-foreground">Client</span>, select the internal firm account (typically something like "Carpenter & Company — Internal").</li>
        <li>Enter your hours (typically 8 for a full day).</li>
        <li>Save the entry.</li>
      </ol>
      <Tip>Log PTO on or before the last day of the pay period so it's included when you submit.</Tip>
    </Section>

    <Section title="PTO Balance Page">
      <p>Go to <span className="font-medium text-foreground">Time → PTO</span> to see your balance summary:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Available Balance</span> — your current accrued PTO minus used time.</li>
        <li><span className="font-medium text-foreground">Accrued YTD</span> — how much you've earned so far this calendar year.</li>
        <li><span className="font-medium text-foreground">Used YTD</span> — PTO entries that have been approved.</li>
        <li><span className="font-medium text-foreground">Pending</span> — PTO entries not yet approved.</li>
      </ul>
      <p className="mt-2">Below the cards, a history table lists every PTO and holiday entry for the year with their status.</p>
    </Section>

    <Section title="Holidays">
      <p>Firm holidays are logged the same way as PTO but using the <span className="font-medium text-foreground">Holiday</span> activity code. Your manager will communicate which days are firm holidays. Log them as entries to keep your timesheet complete and your balance accurate.</p>
    </Section>

    <Note>PTO entries follow the same submit → approve workflow as regular time entries. Your PTO balance reflects approved entries, not just ones you've logged.</Note>
  </div>
);

const PayPeriodsContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      Carpenter &amp; Company uses a semi-monthly pay schedule with two pay periods per month — Period A and Period B.
    </p>

    <Section title="Pay Period Schedule">
      <div className="overflow-x-auto mt-2">
        <table className="w-full text-xs border border-border rounded-md overflow-hidden">
          <thead className="bg-muted">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-foreground">Period</th>
              <th className="text-left px-3 py-2 font-semibold text-foreground">Work Dates</th>
              <th className="text-left px-3 py-2 font-semibold text-foreground">Pay Date</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-border">
              <td className="px-3 py-2 font-medium text-foreground">A</td>
              <td className="px-3 py-2">25th of prior month → 8th of current month</td>
              <td className="px-3 py-2">15th of current month</td>
            </tr>
            <tr className="border-t border-border bg-muted/30">
              <td className="px-3 py-2 font-medium text-foreground">B</td>
              <td className="px-3 py-2">9th → 24th of current month</td>
              <td className="px-3 py-2">Last day of current month</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-3">Example: For June pay, Period A covers May 25 – June 8 (paid June 15), and Period B covers June 9 – June 24 (paid June 30).</p>
    </Section>

    <Section title="Period Status">
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Open</span> — you can log and edit draft entries freely.</li>
        <li><span className="font-medium text-foreground">Submitted</span> — your entries are in review. No edits until approved or rejected.</li>
        <li><span className="font-medium text-foreground">Closed</span> — the period has been finalized by an admin. All entries are locked permanently.</li>
      </ul>
    </Section>

    <Section title="Closed Periods">
      <p>Once a pay period is closed, all entries become <span className="font-medium text-foreground">immutable</span> — they cannot be edited, deleted, or resubmitted. This protects payroll integrity.</p>
      <p className="mt-1">If you need a correction on a closed period, contact an admin — only they can reopen a period, and it should be done with care.</p>
      <Note>Periods are typically closed by an admin after payroll runs. They do not close automatically.</Note>
    </Section>

    <Section title="Navigating Periods">
      <p>On both the Timesheet and Today pages, use the <span className="font-medium text-foreground">&lt; &gt;</span> arrows to move between periods. The current period is highlighted. Past and future periods are accessible for reference, but only the current open period accepts new entries.</p>
    </Section>
  </div>
);

const StatusMeaningsContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      Every time entry has a status that reflects where it is in the review lifecycle. Here's what each status means and what you can do.
    </p>

    <Section title="Draft">
      <p>A newly saved entry starts as <span className="font-medium text-foreground">Draft</span>. This is your working state.</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li>You can <span className="font-medium text-foreground">edit</span> the entry (date, hours, code, notes, billable).</li>
        <li>You can <span className="font-medium text-foreground">delete</span> the entry.</li>
        <li>Draft entries are <span className="font-medium text-foreground">not visible to managers</span> for approval until you submit.</li>
      </ul>
    </Section>

    <Section title="Submitted">
      <p>After you click Submit on the Submit page, all drafts move to <span className="font-medium text-foreground">Submitted</span>.</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li>The entry is <span className="font-medium text-foreground">locked</span> — you cannot edit or delete it.</li>
        <li>Your manager can see it on the Approvals page.</li>
        <li>Awaiting approval or rejection.</li>
      </ul>
    </Section>

    <Section title="Approved">
      <p><span className="font-medium text-foreground">Approved</span> means your manager has reviewed and signed off on the entry.</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li>The entry is <span className="font-medium text-foreground">permanently locked</span>.</li>
        <li>It is included in payroll reports and utilization metrics.</li>
        <li>No further changes are possible (unless a manager reopens the period, which is rare).</li>
      </ul>
    </Section>

    <Section title="Rejected">
      <p><span className="font-medium text-foreground">Rejected</span> means your manager flagged the entry and sent it back.</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li>The entry returns to <span className="font-medium text-foreground">Draft</span> status.</li>
        <li>A rejection note from your manager explains what to fix.</li>
        <li>You can edit the entry and resubmit.</li>
      </ul>
      <Tip>After fixing a rejected entry, go back to Time → Submit to resubmit it. Only draft entries appear on that page, so it's easy to spot anything that needs attention.</Tip>
    </Section>

    <Section title="Status at a Glance">
      <div className="overflow-x-auto mt-2">
        <table className="w-full text-xs border border-border rounded-md overflow-hidden">
          <thead className="bg-muted">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-foreground">Status</th>
              <th className="text-left px-3 py-2 font-semibold text-foreground">Can Edit?</th>
              <th className="text-left px-3 py-2 font-semibold text-foreground">Can Delete?</th>
              <th className="text-left px-3 py-2 font-semibold text-foreground">Visible to Manager?</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-border">
              <td className="px-3 py-2 font-medium text-foreground">Draft</td>
              <td className="px-3 py-2 text-green-700">Yes</td>
              <td className="px-3 py-2 text-green-700">Yes</td>
              <td className="px-3 py-2 text-muted-foreground">No</td>
            </tr>
            <tr className="border-t border-border bg-muted/30">
              <td className="px-3 py-2 font-medium text-foreground">Submitted</td>
              <td className="px-3 py-2 text-red-600">No</td>
              <td className="px-3 py-2 text-red-600">No</td>
              <td className="px-3 py-2 text-green-700">Yes</td>
            </tr>
            <tr className="border-t border-border">
              <td className="px-3 py-2 font-medium text-foreground">Approved</td>
              <td className="px-3 py-2 text-red-600">No</td>
              <td className="px-3 py-2 text-red-600">No</td>
              <td className="px-3 py-2 text-green-700">Yes</td>
            </tr>
            <tr className="border-t border-border bg-muted/30">
              <td className="px-3 py-2 font-medium text-foreground">Rejected</td>
              <td className="px-3 py-2 text-green-700">Yes (back to Draft)</td>
              <td className="px-3 py-2 text-green-700">Yes</td>
              <td className="px-3 py-2 text-muted-foreground">No</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Section>
  </div>
);

const ClientsContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      The Clients section is your firm's account directory. Each account has a full profile with
      contacts, projects, tasks, notes, activity history, and reference information.
    </p>

    <Section title="The Client List">
      <p>Go to <span className="font-medium text-foreground">Clients → Accounts</span> to see all firm clients. Each row shows the client name, status, primary contact, phone, and portal access.</p>
      <p className="mt-2">Filter and search options:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Search</span> — searches by account name, contact name, email, or phone.</li>
        <li><span className="font-medium text-foreground">Status</span> — Active, Inactive, or All.</li>
        <li><span className="font-medium text-foreground">Assignee</span> — show only accounts assigned to you or a specific team member.</li>
      </ul>
      <p className="mt-2">Click any row to open the client's full profile.</p>
    </Section>

    <Section title="Client Profile — Overview Tab">
      <p>The Overview tab shows the client's open projects at a glance. Click <span className="font-medium text-foreground">View all projects</span> to jump to the Projects page filtered to that client, or expand individual projects to see their task progress inline.</p>
    </Section>

    <Section title="Client Profile — Notes Tab">
      <p>Client notes are freeform records attached to the account — meeting summaries, preferences, instructions, anything worth remembering. Pinned notes appear at the top.</p>
      <p className="mt-1">The tab badge shows the total note count so you can quickly tell if a client has documented history before making contact.</p>
    </Section>

    <Section title="Client Profile — Activity Tab">
      <p>The Activity tab shows recent time entries logged against this client — who worked on what, when, and for how long. Useful for a quick "what has happened recently" view without running a full report.</p>
    </Section>

    <Section title="Client Profile — Tasks Tab">
      <p>All open tasks across every project for this client in one place. Each task shows its name, associated project, due date, and status. Click a task to open the task detail panel.</p>
    </Section>

    <Section title="Right Sidebar — Contacts">
      <p>Lists the client's known contacts with name, email, phone, and mobile. The primary contact is marked with a badge. Email addresses are clickable (opens your mail client).</p>
    </Section>

    <Section title="Right Sidebar — Assignees">
      <p>Team members responsible for this account. Use the <span className="font-medium text-foreground">Add Staff</span> button to assign someone. Assigned staff see this client under "My Accounts" in the Assignee filter.</p>
      <Tip>Keep assignees current — they drive the Assignee filter on both the Clients and Projects pages, so staff can quickly find their own workload.</Tip>
    </Section>

    <Section title="Right Sidebar — Relationships">
      <p>Links this account to other accounts with a labeled relationship (Owner, Spouse, Partner, etc.). Useful for family groups, business partners, or entities with shared ownership.</p>
      <p className="mt-1">Each relationship is visible on both accounts. Click the linked account name to navigate directly to their profile.</p>
    </Section>

    <Section title="Right Sidebar — About">
      <p>Reference fields for the account — collapsed by default, click to expand:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Entity Type</span> — Individual, S-Corp, Partnership, etc.</li>
        <li><span className="font-medium text-foreground">EIN / SSN (last 4)</span> — tax ID information.</li>
        <li><span className="font-medium text-foreground">Formation Date / S-Election Date</span> — corporate history.</li>
        <li><span className="font-medium text-foreground">Address</span> — mailing address.</li>
        <li><span className="font-medium text-foreground">Website</span> — opens in a new tab.</li>
        <li><span className="font-medium text-foreground">SmartVault URL</span> — direct link to the client's SmartVault folder.</li>
      </ul>
      <Note>About fields are imported from Financial Cents and are read-only in this view. Contact an admin if a field needs correction.</Note>
    </Section>

    <Section title="Right Sidebar — Resources">
      <p>A freeform list of labeled links specific to this client — bookmarks, portals, reference documents, anything with a URL. Click <span className="font-medium text-foreground">Add Resource</span> to create one. Each entry has a label and a URL that opens in a new tab.</p>
    </Section>

    <Section title="Client Status">
      <p>Each account is either <span className="font-medium text-foreground">Active</span> or <span className="font-medium text-foreground">Inactive</span>. Inactive accounts are hidden from default list views but remain in the system for historical reference. Toggle status by clicking the badge on the client list row.</p>
    </Section>

    <Section title="Client Portal Access">
      <p>Clients can be invited to the self-service portal where they can view their projects, tasks, and upload requested documents. To invite a client:</p>
      <ol className="list-decimal pl-5 space-y-1 mt-1">
        <li>On the Accounts list, click the portal link icon on the client's row.</li>
        <li>Enter the client's email address.</li>
        <li>An invitation link is generated — copy and send it, or the system emails it directly.</li>
      </ol>
      <p className="mt-2">The dialog also shows existing invitations with their status (Pending / Accepted) and lets you revoke any that haven't been used.</p>
    </Section>
  </div>
);

const ProjectsContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      Projects represent active engagements for a client — a tax return, an audit, a bookkeeping
      engagement, etc. Each project can have tasks, assignees, and a due date.
    </p>

    <Section title="Creating a Project">
      <ol className="list-decimal pl-5 space-y-2 mt-1">
        <li>From the <span className="font-medium text-foreground">Projects</span> page (the home page after login), click <span className="font-medium text-foreground">New Project</span>.</li>
        <li>Fill in the <span className="font-medium text-foreground">Title</span> and select an <span className="font-medium text-foreground">Account</span> (client) — both are required.</li>
        <li>Optionally set a <span className="font-medium text-foreground">Status</span>, <span className="font-medium text-foreground">Due Date</span>, and a <span className="font-medium text-foreground">Template</span> to pre-populate tasks.</li>
        <li>Click <span className="font-medium text-foreground">Save</span>.</li>
      </ol>
      <Tip>Using a template when creating a project automatically generates a standard task checklist for that engagement type (e.g., all steps for a 1040 tax return). This saves time and keeps the team consistent.</Tip>
    </Section>

    <Section title="Project Statuses">
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Open</span> — work hasn't started yet.</li>
        <li><span className="font-medium text-foreground">In Progress</span> — actively being worked.</li>
        <li><span className="font-medium text-foreground">Completed</span> — work is done.</li>
        <li><span className="font-medium text-foreground">Closed</span> — finalized and archived.</li>
      </ul>
      <p className="mt-2">Change a project's status by opening the edit modal (click the edit icon on the project row).</p>
    </Section>

    <Section title="Filtering & Finding Projects">
      <p>The Projects page has several ways to narrow the list:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Search</span> — type part of a project title.</li>
        <li><span className="font-medium text-foreground">Status filter</span> — show All, Open, In Progress, or Closed.</li>
        <li><span className="font-medium text-foreground">Account filter</span> — show only projects for a specific client.</li>
        <li><span className="font-medium text-foreground">Assignee filter</span> — My Projects, Unassigned, or a specific team member.</li>
        <li><span className="font-medium text-foreground">Due date filters</span> — Today, This Week, Next Week, Overdue. These can be combined.</li>
      </ul>
      <p className="mt-2">By default, only active (non-closed) projects are shown. Toggle <span className="font-medium text-foreground">All</span> to include closed and completed ones.</p>
    </Section>

    <Section title="Viewing Tasks on a Project">
      <p>Click any project row to expand it and see a summary of its tasks — how many are complete vs. total. This gives a quick progress snapshot without leaving the Projects page.</p>
      <p className="mt-1">For full task detail, go to the <span className="font-medium text-foreground">Tasks</span> page and filter by that project, or open the client's profile and navigate to the Tasks tab.</p>
    </Section>

    <Section title="Project Assignees">
      <p>You can add team members to a project from the edit modal. Assignees appear in the Assignee filter and are used in utilization reporting. Adding yourself to a project is how it shows up under "My Projects."</p>
    </Section>

    <Section title="Editing & Deleting Projects">
      <p>Click the edit icon on any project row to open the edit modal. All fields can be changed. To delete a project, open the edit modal and click <span className="font-medium text-red-600">Delete</span> at the bottom — this is permanent and removes all associated tasks.</p>
      <Note>Deleting a project does not delete time entries already logged against it. Those remain in the time tracking system linked to the client and activity code.</Note>
    </Section>

    <Section title="Recurring Projects">
      <p>A recurring project automatically generates its next instance once the current one is finished. When all tasks in a recurring project are marked done, CentCom immediately creates the next project with the correct period label and due date — you don't need to do anything manually.</p>
      <p className="mt-2">As a backup, the system also checks for overdue spawns every night and creates any missing successor projects automatically.</p>
      <p className="mt-2">You can tell a project is recurring by the <span className="font-medium text-foreground">↺ monthly</span> (or weekly, quarterly, etc.) badge on its row. The badge shows the pattern, and a small <span className="font-medium text-foreground">next: May 31</span> label shows when the successor is scheduled to be created.</p>
      <p className="mt-2">The successor is a full copy of the current project — all sections, tasks, subtasks, assignees, and financial source links carry forward exactly as they are. Any customizations your team made (renamed tasks, added tasks, linked sources) are preserved every cycle.</p>
      <Tip>Recurring projects can be freely closed or completed once the work is done — this does not break the chain. The successor carries the schedule forward on its own.</Tip>
    </Section>

    <Section title="Spawning a Successor Manually">
      <p>If a recurring project's successor wasn't created automatically (for example, because the project was closed before all tasks were finished, or a chain was broken during a system issue), managers and admins can spawn the next instance on demand.</p>
      <ol className="list-decimal pl-5 space-y-1 mt-1">
        <li>Find the recurring project on the Projects page — look for the <span className="font-medium text-foreground">↺</span> badge.</li>
        <li>Click the <span className="font-medium text-foreground">pencil (edit) icon</span> on the project row to open the Edit modal.</li>
        <li>Click <span className="font-medium text-foreground">Spawn Now</span> at the bottom of the modal — a new successor project is created immediately with the correct next period and due date.</li>
      </ol>
      <Note>Spawn Now is only visible to managers and admins. Use it when you notice a gap in the recurring chain — for example, May's bookkeeping project exists but June's was never created.</Note>
    </Section>
  </div>
);

const TasksContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      Tasks are the individual steps within a project. They can be assigned, given due dates,
      commented on, and used to track client file uploads.
    </p>

    <Section title="Finding Your Tasks">
      <p>Go to <span className="font-medium text-foreground">Tasks</span> in the sidebar (under Workflow). Tasks are grouped by Account → Project → Task for easy scanning.</p>
      <p className="mt-2">Use the filters to narrow the list:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">My Tasks</span> (assignee filter) — shows only tasks assigned to you.</li>
        <li><span className="font-medium text-foreground">Status filter</span> — Open, In Progress, Done, or All.</li>
        <li><span className="font-medium text-foreground">Due date filters</span> — Today, This Week, This Month, Overdue.</li>
        <li><span className="font-medium text-foreground">Search</span> — filter by task title.</li>
      </ul>
      <Tip>Start your day by selecting "My Tasks" + "This Week" to see everything you own that's due soon.</Tip>
    </Section>

    <Section title="Task Statuses">
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Open</span> — task exists but hasn't been started.</li>
        <li><span className="font-medium text-foreground">In Progress</span> — actively being worked on.</li>
        <li><span className="font-medium text-foreground">On Hold</span> — paused, waiting on something external.</li>
        <li><span className="font-medium text-foreground">Done</span> — completed. Tasks marked Done are hidden from the default active view.</li>
      </ul>
      <p className="mt-2">Change a task's status from the task detail panel, or click the quick <span className="font-medium text-foreground">Complete</span> button on the task row to mark it Done immediately.</p>
    </Section>

    <Section title="Task Detail Panel">
      <p>Click any task row (or the Details button) to open the detail panel on the right side. From here you can:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li>Edit the <span className="font-medium text-foreground">title, description, status, due date</span>, and <span className="font-medium text-foreground">assigned user</span>.</li>
        <li>Set an <span className="font-medium text-foreground">Internal Due Date</span> — a staff-only deadline separate from the client-facing due date.</li>
        <li>Toggle <span className="font-medium text-foreground">Allow Client Upload</span> to let clients attach files to this task via the client portal.</li>
        <li>View and add <span className="font-medium text-foreground">comments</span>.</li>
        <li>Preview or download <span className="font-medium text-foreground">attachments</span>.</li>
      </ul>
    </Section>

    <Section title="Comments">
      <p>The comments section in the task detail panel is for internal team discussion. Type your note and click <span className="font-medium text-foreground">Add Comment</span>.</p>
      <p className="mt-1">The <span className="font-medium text-foreground">Visible to client</span> toggle makes a comment visible to the client through the client portal. Leave it off for internal notes only.</p>
      <Note>Comments cannot be edited or deleted after posting — write carefully, especially if marking a comment as client-visible.</Note>
    </Section>

    <Section title="Attachments">
      <p>Files attached to a task (by staff or clients) appear in the task detail panel. Supported file types include PDF, images (JPG/PNG), Excel, Word, and CSV.</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li>Click <span className="font-medium text-foreground">Preview</span> to view the file inline without downloading.</li>
        <li>Click <span className="font-medium text-foreground">Download</span> to save a copy locally.</li>
      </ul>
    </Section>

    <Section title="Creating Tasks Manually">
      <p>Click <span className="font-medium text-foreground">New Task</span> on the Tasks page. You must select a <span className="font-medium text-foreground">Project</span> to attach the task to. Fill in the title, optional due date, and assignee, then save.</p>
      <Tip>Most tasks are created automatically when a project is created from a template. Manual task creation is best for one-off items that don't fit the standard checklist.</Tip>
    </Section>

    <Section title="Overdue Tasks">
      <p>Tasks with a past due date that aren't Done are highlighted in <span className="font-medium text-red-600 font-medium">red</span> in the task list. Use the <span className="font-medium text-foreground">Overdue</span> due-date filter to see only these tasks across all your projects.</p>
    </Section>
  </div>
);

const ClientGroupsContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      Client Groups let you organize accounts into named sets — for example, "Family Tax Clients,"
      "Business Entities," or "Christmas Card List." Groups are managed by admins and visible
      throughout the Clients section.
    </p>

    <Section title="Viewing Groups">
      <p>Navigate to <span className="font-medium text-foreground">Clients → Client Groups</span> in
      the sidebar to see all groups. Each group card shows its name and member count. Click a group
      to view its members.</p>
    </Section>

    <Section title="Creating a Group">
      <p>From the Client Groups page, click <span className="font-medium text-foreground">New Group</span>.
      Give it a name and an optional description, then save. You can add members immediately or after creation.</p>
    </Section>

    <Section title="Adding & Removing Members">
      <p>Open a group and use the member controls to add or remove accounts. Members are linked by
      account — not by contact — so each group entry corresponds to a client account record in the system.</p>
      <Tip>You can add the same account to multiple groups. Groups are non-exclusive.</Tip>
    </Section>

    <Section title="Importing Groups from Financial Cents">
      <p>If you're migrating from Financial Cents, groups can be imported via
      <span className="font-medium text-foreground"> Admin → Import → Client Groups</span>. Paste the
      group JSON captured from browser devtools (FC network tab) into the import panel. Groups are
      matched by name and deduped on re-import — safe to run more than once.</p>
      <Note>Groups with clients not in your accounts list will show warnings but still import the group
      itself. For example, the "Christmas Card List" may include contacts or inactive clients that aren't
      in the system — this is expected and non-fatal.</Note>
    </Section>
  </div>
);

const MessagesContent = () => (
  <div className="space-y-4 text-sm text-foreground">
    <Section title="What is Project Messaging?">
      <p>Every project has its own message thread — a running conversation between you and your team
      (and optionally the client). Use it to ask questions, share updates, or leave notes that stay
      with the project instead of getting buried in email.</p>
    </Section>

    <Section title="Opening a Project's Messages">
      <p>There are two ways to open a project's message thread:</p>
      <ul className="list-disc pl-4 space-y-1 mt-1">
        <li><strong>Projects page</strong> — find the project row, click the chat bubble icon (<MessageSquare className="w-3.5 h-3.5 inline -mt-0.5" />) to the left of the Edit button. A slide-over panel opens on the right.</li>
        <li><strong>Client profile → Overview tab</strong> — each project row has the same chat bubble icon.</li>
      </ul>
    </Section>

    <Section title="Sending a Message">
      <p>Type your message in the compose area at the bottom of the panel, then click <strong>Send</strong> (or press <strong>⌘+Enter</strong> / <strong>Ctrl+Enter</strong>).</p>
      <Tip>Check <strong>"Also visible to client"</strong> before sending if you want the client to see
      the message in their portal. Internal messages (unchecked) are staff-only and never visible to
      the client.</Tip>
    </Section>

    <Section title="Client-Visible Messages">
      <p>Messages marked <strong>"Also visible to client"</strong> appear in the portal under the project's
      <em> Messages</em> tab. The client can reply directly from the portal and their reply appears
      in the same thread for the whole team to see.</p>
      <Note>Only messages explicitly marked client-visible are shared. Internal team discussion stays
      private by default.</Note>
    </Section>

    <Section title="Client View (Portal)">
      <p>When a client opens a project in the portal, they see a <strong>Tasks</strong> tab and a
      <strong>Messages</strong> tab. The Messages tab shows all client-visible messages from your team,
      with a reply box at the bottom. Replies from clients are instantly visible to staff in the same
      thread.</p>
    </Section>

    <Section title="Live Updates">
      <p>The message thread refreshes every 30 seconds automatically. If you're waiting on a reply,
      just leave the panel open — new messages will appear without reloading the page.</p>
    </Section>
  </div>
);

const EmailsContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      The Emails tab on a client profile shows email history from your Microsoft 365 mailboxes
      involving that client. No manual forwarding or CC-ing required — emails are pulled directly
      from Outlook via Microsoft Graph.
    </p>

    <Section title="How It Works">
      <p>When you open a client's Emails tab, CommandHub searches your firm's configured M365 mailboxes
      for any emails where the client's contacts appear as sender, recipient, or CC. Results are merged
      and deduplicated across all mailboxes.</p>
      <ul className="list-disc pl-5 space-y-1 mt-2">
        <li><strong>Personal staff mailboxes</strong> — searched by contact email address (e.g. <em>participants: client@example.com</em>).</li>
        <li><strong>Client-dedicated shared mailboxes</strong> — all messages fetched directly (no search filter needed — the entire mailbox belongs to that client).</li>
        <li><strong>Tammy Tasker</strong> — treated as a personal mailbox; searched by contact email.</li>
      </ul>
      <Tip>Emails don't load automatically — click <strong>Refresh</strong> on the Emails tab to trigger a live search. This avoids unnecessary Microsoft Graph API calls when you're not looking at email.</Tip>
    </Section>

    <Section title="Search & Filter">
      <p>Use the filter bar at the top of the Emails tab to narrow results:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><strong>Keyword</strong> — searches subject lines and message previews.</li>
        <li><strong>From date / To date</strong> — filter by received date range.</li>
        <li><strong>Sort</strong> — Newest first, Oldest first, Sender A–Z, or Pinned first.</li>
      </ul>
      <p className="mt-2">Results load 25 at a time. Click <strong>Show more</strong> at the bottom to load the next batch.</p>
    </Section>

    <Section title="Reading an Email">
      <p>Click any email row to expand it and read the full body. HTML emails render inline; plain-text
      emails appear in a preformatted block. The sender, date, and recipient list are shown in the header.</p>
      <p className="mt-1">Each email row shows a grey badge indicating which mailbox it came from (e.g. <em>wcarpenter</em>).
      An unread dot appears for unread messages, and a paperclip icon marks emails with attachments.</p>
    </Section>

    <Section title="Pinning Emails to Projects">
      <p>You can attach important emails to a specific project for reference:</p>
      <ol className="list-decimal pl-5 space-y-1 mt-1">
        <li>Expand the email.</li>
        <li>Select a project from the <strong>Pin to project</strong> dropdown.</li>
        <li>Click the paperclip icon to pin.</li>
      </ol>
      <p className="mt-2">Pinned emails appear in the project's message sheet under the <strong>Pinned Emails</strong>
      section (below the chat thread). Click the × next to a pinned email to unpin it.</p>
      <Tip>Pin the email where the client sent in their tax documents to the corresponding tax project — keeps all context in one place.</Tip>
    </Section>

    <Section title="Mailbox Setup (Admin)">
      <p>Admins configure which M365 mailboxes are searched in <strong>Admin → Settings → Microsoft 365 Email Accounts</strong>.</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><strong>Personal staff mailboxes</strong> — add each team member's email. No "Shared mailbox" checkbox. Emails searched by client contact address.</li>
        <li><strong>Shared mailboxes dedicated to a client</strong> — check "Shared mailbox" and link to the client account. Fetches all messages directly.</li>
        <li><strong>Tammy Tasker</strong> — add as a regular mailbox (no checkbox). Emails searched by client contact address like any personal mailbox.</li>
      </ul>
      <Note>Microsoft Graph application permissions must be granted in Azure for all mailboxes — no per-user sign-in required. Contact your Azure admin if emails are missing or the tab shows a configuration error.</Note>
    </Section>
  </div>
);

const TagsContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      Tags are colored labels you can attach to projects, tasks, and sub-tasks. Use them to filter your
      work view, group related items across clients, and trigger automatic actions.
    </p>

    <Section title="What Are Tags?">
      <p>A tag is a short, colored label — like "1040", "Rush", or "Needs Review" — that you can attach
      to any project or task. Tags show as small colored chips on the row. Click a chip's × to remove it,
      or use the tag icon (⊞) to add new ones.</p>
      <Tip>Tags are shared across the whole firm. Create them once in <span className="font-medium text-foreground">Settings → Tags</span>, then use them everywhere.</Tip>
    </Section>

    <Section title="Creating & Managing Tags">
      <p>Only admins can create, rename, or delete tags. Go to <span className="font-medium text-foreground">Settings → Tags</span> to manage the tag library.
      Each tag has a name, a hex color, and optional description. Tags marked <span className="font-medium text-foreground">Permanent</span> cannot be accidentally deleted.</p>
    </Section>

    <Section title="Applying Tags Manually">
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Projects</span> — on the Projects page, the tag icon (⊞) appears in the row actions area. Click it to open the tag picker. Applied tags appear as chips next to the project name.</li>
        <li><span className="font-medium text-foreground">Tasks</span> — expand a task row to see the tag icon before the assignee avatars. Applied chips show inline on the row.</li>
        <li><span className="font-medium text-foreground">Sub-tasks</span> — each sub-task row has its own tag picker.</li>
      </ul>
    </Section>

    <Section title="Automatic Tags">
      <p>CentCom can apply and remove tags automatically in four ways:</p>

      <p className="mt-3 font-medium text-foreground text-xs uppercase tracking-wide">1 — Project Type Tags (no setup required)</p>
      <p className="mt-1">When you set a project's type (e.g. 1040, 1120S, Bookkeeping), CentCom automatically creates
      a matching tag if one doesn't exist and applies it to the project. Each type gets a consistent color so your
      project list is color-coded at a glance.</p>
      <Tip>This fires on both create and edit — updating a project's type will auto-apply the new type tag.</Tip>

      <p className="mt-3 font-medium text-foreground text-xs uppercase tracking-wide">2 — Template Default Tags</p>
      <p className="mt-1">When a project is created from a template, any tags set as defaults on that template are
      automatically applied. Admins can configure these in <span className="font-medium text-foreground">Admin → Templates → open a template → Default Tags section</span>.</p>

      <p className="mt-3 font-medium text-foreground text-xs uppercase tracking-wide">3 — Status-Triggered Rules</p>
      <p className="mt-1">When a project moves to a workflow status — whether manually or via auto-advance — CentCom
      checks for tag rules tied to that status and applies or removes the specified tags. Configure rules in
      <span className="font-medium text-foreground"> Settings → Tag Rules</span> (admin only). Each rule pairs a status
      with a tag and an action (Add or Remove).</p>
      <Note>Tag rules are additive — applying a rule never removes other tags. Only rules with action = "Remove" remove a tag.</Note>

      <p className="mt-3 font-medium text-foreground text-xs uppercase tracking-wide">4 — Task-Completion Triggered</p>
      <p className="mt-1">When all tasks in a section are completed, the project's workflow status auto-advances. Any
      tag rules for the new status fire immediately — so task completion can indirectly trigger tag changes without
      any manual steps.</p>
    </Section>
  </div>
);

const WorkflowContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      Workflow Pipelines track where a project stands in your firm's process — from intake all the way
      through filing or final delivery. Statuses advance automatically as tasks are completed, and the
      right person is notified when work lands in their queue.
    </p>

    <Section title="What Is a Pipeline?">
      <p>A pipeline is a named sequence of statuses that a project moves through. CentCom ships with
      three built-in pipelines:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Individual Tax Return</span> — 21 statuses from Intake through E-file Acceptance.</li>
        <li><span className="font-medium text-foreground">Business Tax Return</span> — 21 statuses, same structure as Individual.</li>
        <li><span className="font-medium text-foreground">Bookkeeping</span> — 14 statuses from Intake through Delivery.</li>
      </ul>
      <p className="mt-2">Each status belongs to one of three types:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Normal</span> — regular numbered steps in the workflow (e.g., Preparation, Manager Review).</li>
        <li><span className="font-medium text-foreground">Interrupt</span> — pause states like Hold, Extension Requested, or Need Info. When you enter one, CentCom remembers where you were so you can resume.</li>
        <li><span className="font-medium text-foreground">Terminal</span> — end states such as Complete or E-file Acceptance. Moving to a terminal status clears the interrupt memory.</li>
      </ul>
    </Section>

    <Section title="The Status Badge">
      <p>Every project row on the Projects page shows a colored status badge. The color corresponds to
      the status type and pipeline. Click the badge to open the status picker.</p>
      <p className="mt-2">Inside the picker you'll see three sections:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Numbered normal statuses</span> — the main progression. The current status has a checkmark.</li>
        <li><span className="font-medium text-foreground">Pause / Interrupt</span> — put the project on hold without losing your place.</li>
        <li><span className="font-medium text-foreground">End States</span> — mark the project as fully complete.</li>
      </ul>
    </Section>

    <Section title="Automatic Status Advancement">
      <p>You don't need to manually move statuses. When you check off the last task in a section,
      CentCom checks whether that section's name matches the current pipeline status. If it does —
      and all tasks in that section are complete — the project automatically advances to the next
      normal status.</p>
      <Tip>
        Section names and status names are matched case-insensitively. A section called
        "Manager Review" will match a status called "manager review" or "Manager Review."
        Spaces around slashes are also normalized (e.g., "Prep / Review" = "prep/review").
      </Tip>
      <p className="mt-2">For projects whose tasks have no section grouping, CentCom falls back to
      checking whether <em>all</em> root tasks on the project are done before advancing.</p>
    </Section>

    <Section title="Automatic Tag Rules">
      <p>Alongside auto-advancing the status, CentCom can also add or remove project tags whenever a
      status changes. Set these rules in <span className="font-medium text-foreground">Settings → Tag Rules</span>:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li>Pick a pipeline → expand a status → add a rule specifying a tag and whether to <span className="font-medium text-foreground">Add</span> or <span className="font-medium text-foreground">Remove</span> it.</li>
        <li>Rules fire on both manual status changes and auto-advances triggered by task completion.</li>
        <li>Multiple rules per status are supported — they all fire together.</li>
      </ul>
      <Tip>Use "Remove" rules to clean up temporary tags (e.g., remove "In Preparation" when moving past that status) so your tag view stays tidy.</Tip>
    </Section>

    <Section title="Walkthrough — A 1040 Moving Through Preparation">
      <ol className="list-decimal pl-5 space-y-2 mt-1">
        <li>A new 1040 project is created. The status badge shows <span className="font-medium text-foreground">Intake</span> (status 1).</li>
        <li>The preparer opens the project and sees the task list. Tasks in the <span className="font-medium text-foreground">Intake</span> section include "Gather documents" and "Confirm prior year info."</li>
        <li>The preparer checks off both tasks. CentCom detects that all Intake-section tasks are done.</li>
        <li>The project automatically advances to <span className="font-medium text-foreground">Preparation</span> (status 2). The status badge updates in real time.</li>
        <li>The assigned Preparer receives an email: <em>"Smith, John → Preparation — now assigned to you."</em></li>
        <li>The preparer works through the Preparation section tasks. When the last one is checked, the status moves to <span className="font-medium text-foreground">Manager Review</span> and the assigned Manager is notified.</li>
      </ol>
      <Note>Only the person whose <span className="font-medium text-foreground">Workflow Role</span> on
      that client matches the status's notification role receives an email. Set workflow roles on the
      client profile's Assignees sidebar (Account Manager, Preparer, Checker, Reviewer, etc.).</Note>
    </Section>

    <Section title="Interrupt Statuses (Holds & Extensions)">
      <p>Need to pause a project? Click the status badge and choose an interrupt status:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Hold</span> — waiting on the client or an external factor.</li>
        <li><span className="font-medium text-foreground">Extension Requested</span> — a filing extension has been filed.</li>
        <li><span className="font-medium text-foreground">Need Info</span> — information is missing before work can continue.</li>
      </ul>
      <p className="mt-2">When you enter an interrupt status, CentCom saves your prior status automatically. A
      <span className="font-medium text-foreground"> Resume</span> button (with a ↩ icon) appears at the top of
      the status picker — click it to jump straight back to where you left off.</p>
      <Tip>Tasks completed while a project is in an interrupt status will not trigger automatic advancement
      — the project must be manually resumed first.</Tip>
    </Section>

    <Section title="Send Back">
      <p>Made a mistake or need to move a project backward? When a project is on a normal status that
      isn't the first status in the pipeline, an amber <span className="font-medium text-foreground">Send back</span> button
      appears at the top of the status picker. Clicking it moves the project one status back — no
      confirmation needed, but the status badge updates immediately so the change is visible.</p>
    </Section>

    <Section title="Setting Workflow Roles">
      <p>Workflow roles determine who gets notified when a status change occurs. Assign roles per
      client, not per project:</p>
      <ol className="list-decimal pl-5 space-y-1 mt-1">
        <li>Open the client's profile (Clients → click a client name).</li>
        <li>In the right sidebar, find the <span className="font-medium text-foreground">Assignees</span> section.</li>
        <li>Below each team member's name, use the role dropdown to set their workflow role:
          Account Manager, Preparer, Checker, Reviewer, Assembler, or Signer.</li>
      </ol>
      <p className="mt-2">If no one on the client has the matching role for a status, the notification is silently skipped — no error, the status still advances.</p>
    </Section>

    <Section title="Linking a Pipeline to a Template">
      <p>When you create projects from a template, you can associate that template with a pipeline.
      This means every project created from that template will automatically inherit the pipeline's
      statuses, and task section names should match status names for automatic advancement to work.</p>
      <p className="mt-2">To set this up: go to <span className="font-medium text-foreground">Admin → Templates</span>,
      open a template, and select the pipeline in the <span className="font-medium text-foreground">Workflow Pipeline</span> dropdown
      in the Settings card.</p>
    </Section>
  </div>
);

const DocumentsContent = () => (
  <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
    <Section title="Client Documents Tab">
      <p>Every client profile has a <span className="font-medium text-foreground">Documents</span> tab (last tab in the tab bar). This is your central file storage for each client — tax returns, engagement letters, payroll records, correspondence, contracts, and more. Files are stored in Cloudflare R2 (SOC 2 compliant, zero egress fees).</p>
    </Section>
    <Section title="Uploading Files">
      <p>Click the <span className="font-medium text-foreground">Upload</span> button in the top right of the Documents tab. Select any file from your computer. The file uploads directly to secure cloud storage — it never routes through the Central Command server. Supported: PDF, Word, Excel, PowerPoint, images, and any other file type.</p>
      <p>Select a folder tab first (Tax Returns, Payroll, etc.) before uploading to automatically file it in the right place. You can always move it later.</p>
    </Section>
    <Section title="Folders">
      <p>Documents are organized into 7 folders:</p>
      <ul className="list-disc pl-4 space-y-1">
        <li><span className="font-medium text-foreground">Tax Returns</span> — filed returns, extensions, estimates</li>
        <li><span className="font-medium text-foreground">Engagement Letters</span> — signed agreements</li>
        <li><span className="font-medium text-foreground">Payroll</span> — payroll reports, filings</li>
        <li><span className="font-medium text-foreground">Correspondence</span> — IRS letters, client emails saved as PDF</li>
        <li><span className="font-medium text-foreground">Contracts</span> — agreements, W-9s</li>
        <li><span className="font-medium text-foreground">Working Papers</span> — trial balances, workpapers</li>
        <li><span className="font-medium text-foreground">General</span> — everything else</li>
      </ul>
    </Section>
    <Section title="Previewing Documents">
      <p>Click a file name or the <span className="font-medium text-foreground">↗ icon</span> to preview without leaving the client profile:</p>
      <ul className="list-disc pl-4 space-y-1">
        <li><span className="font-medium text-foreground">PDFs</span> — rendered inline using the browser's built-in PDF viewer</li>
        <li><span className="font-medium text-foreground">Images</span> — displayed full-size in the preview modal</li>
        <li><span className="font-medium text-foreground">Word, Excel, PowerPoint</span> — rendered via Microsoft Office viewer (takes a couple seconds to load)</li>
      </ul>
      <p>Use the <span className="font-medium text-foreground">Download icon</span> to save a file to your computer instead of previewing.</p>
    </Section>
    <Section title="Client Visibility">
      <p>Each document has an <span className="font-medium text-foreground">eye icon</span> toggle. When enabled, the document is marked as visible to the client (for use with the client portal — coming soon). Documents are private to staff by default.</p>
    </Section>
    <Section title="Document Requests">
      <p>Use <span className="font-medium text-foreground">Document Requests</span> (expandable section at the bottom of the tab) to ask a client to provide a specific document. Create a request with a title and folder — it tracks as Pending until fulfilled or cancelled. Useful for tracking outstanding items like "Signed engagement letter" or "2025 W-2s."</p>
    </Section>
  </div>
);

const FinancialSourcesContent = () => (
  <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
    <Section title="What Are Financial Sources?">
      <p>A <span className="font-medium text-foreground">Financial Source</span> is any bank account, credit card, investment account, payroll service, or loan that your team needs to pull statements from regularly — Chase Checking, Wells Fargo Business Savings, ADP Payroll, etc.</p>
      <p>Sources are attached to a client. Open a client profile and find the <span className="font-medium text-foreground">Financial Sources</span> section in the Overview tab (left column) to see or manage their sources.</p>
    </Section>

    <Section title="Adding a Source">
      <p>Click <span className="font-medium text-foreground">Add Source</span> on the Financial Sources section (or on the /sources page). Required fields: Institution Name, Type, and Frequency. Optional fields:</p>
      <ul className="list-disc pl-4 space-y-1">
        <li><span className="font-medium text-foreground">Account Nickname</span> — e.g. "Business Checking" to distinguish multiple accounts at the same bank</li>
        <li><span className="font-medium text-foreground">Hint</span> — last 4 digits or other identifier (e.g. "x1234")</li>
        <li><span className="font-medium text-foreground">Statement Drop Day</span> — day of month when the statement becomes available (1–31)</li>
        <li><span className="font-medium text-foreground">Login URL</span> — direct link to the bank's login page for quick staff access</li>
      </ul>
    </Section>

    <Section title="Tracking Pulls">
      <p>When you download a statement, click the <span className="font-medium text-foreground">checkmark icon</span> on the source row (or the calendar icon on the /sources page). Enter the <span className="font-medium text-foreground">through date</span> — the last date covered by the statement. This updates Last Pulled and Through Date on the source row.</p>
    </Section>

    <Section title="Status Badges">
      <p>Each source has a status badge: <span className="font-medium text-foreground">OK</span>, <span className="font-medium text-foreground">Attention</span>, or <span className="font-medium text-foreground">Overdue</span>. On the /sources page you can click the badge directly to cycle through statuses without opening the edit modal.</p>
    </Section>

    <Section title="Linking a Source to a Task">
      <p>Expand any task (on the Projects page or the client's Tasks tab) and look for the <span className="font-medium text-foreground">Linked Source</span> row. Click <span className="font-medium text-foreground">Link source</span> to attach a financial source to that task — e.g. link a "Download bank statement" sub-task to Chase Checking. The linked source name appears on the task row.</p>
    </Section>

    <Section title="The /Sources Page">
      <p>The standalone <span className="font-medium text-foreground">/sources</span> page (sidebar → Sources) shows sources across all clients. Use the filter bar to narrow by account, type, frequency, status, assigned staff member, or by last-pull and through-date cutoffs. Useful for a firm-wide "what needs pulling this month" view.</p>
    </Section>

    <Section title="Bank Feed — Connecting a Client's Bank">
      <p>Central Command integrates with <span className="font-medium text-foreground">Teller.io</span> to pull live transactions directly from a client's bank account — no manual statement downloads required. This is an <span className="font-medium text-foreground">admin-only</span> feature; staff and managers see no trace of it.</p>
      <p className="mt-2">To connect a bank account to a source:</p>
      <ol className="list-decimal pl-5 space-y-1.5 mt-1">
        <li>Open the client's profile → Financial Sources section.</li>
        <li>Find the source you want to connect (e.g. "Chase Business Checking").</li>
        <li>Click the <span className="font-medium text-foreground">blue link icon</span> on the source row. This generates a one-time connect link and copies it to your clipboard automatically.</li>
        <li>Send that link to the client via email or text.</li>
        <li>The client opens the link and is walked through a secure bank login via the Teller Connect widget — they authenticate directly with their bank. No credentials are ever stored in Central Command.</li>
        <li>Once they complete the flow, the source row shows a green <span className="font-medium text-foreground">Connected</span> badge.</li>
      </ol>
      <Tip>Connect links expire after 7 days. If a client doesn't connect in time, click the blue link icon again to generate a fresh one.</Tip>
    </Section>

    <Section title="Bank Feed — Syncing Transactions">
      <p>Once a source is connected, a green control strip appears below the source row. Use it to manage the bank feed:</p>
      <ul className="list-disc pl-4 space-y-1.5 mt-1">
        <li><span className="font-medium text-foreground">Sync</span> — pulls the latest transactions from Teller. The first sync fetches up to 90 days of history. Subsequent syncs are cursor-based and only pull new transactions since the last sync. The "Synced" timestamp on the source row updates after each successful sync.</li>
        <li><span className="font-medium text-foreground">Transactions</span> — opens the transaction feed inline below the source row. Debits show in red, credits in green. Pending transactions are marked with an amber badge.</li>
      </ul>
      <p className="mt-2">The <span className="font-medium text-foreground">Connected</span> badge also shows the current account balance pulled from Teller at last sync. To see a real-time balance, open the transaction feed and click <span className="font-medium text-foreground">Refresh Balance</span>.</p>
      <Note>Teller currently operates on a developer sandbox for Carpenter &amp; Company. All connections and transaction data are real but the integration is in a controlled testing phase.</Note>
    </Section>

    <Section title="Bank Feed — Multiple Accounts from One Bank Login">
      <p>Some clients have multiple accounts at the same bank under one login — for example, a checking account and a credit card. After connecting, you can track each account separately:</p>
      <ol className="list-decimal pl-5 space-y-1.5 mt-1">
        <li>Click <span className="font-medium text-foreground">Accounts</span> in the green control strip. This opens the Account Manager dialog listing every account Teller sees under that bank login.</li>
        <li>The account currently syncing is labeled <span className="font-medium text-foreground">Syncing</span> (green).</li>
        <li>For any other account, you have two options:
          <ul className="list-disc pl-4 mt-1 space-y-1">
            <li><span className="font-medium text-foreground">Switch sync</span> — makes that account the one this source row syncs going forward (resets the sync cursor).</li>
            <li><span className="font-medium text-foreground">Track separately</span> — creates an additional connection row for that account, linked to a different financial source. Select an existing source from the dropdown, or click <span className="font-medium text-foreground">+ Create new source</span> to make one on the spot without leaving the dialog.</li>
          </ul>
        </li>
      </ol>
      <Tip>If a client has both a business checking and a business credit card at the same bank, connect once and use "Track separately" to split them into two sources — no second client auth needed.</Tip>
    </Section>

    <Section title="Bank Feed — Disconnecting">
      <p>Click <span className="font-medium text-foreground">Disconnect</span> in the green control strip to end the live bank sync. This marks the connection as inactive — no new transactions will pull, but all previously synced transactions are preserved in the transaction feed.</p>
      <p className="mt-1">If you <span className="font-medium text-foreground">delete a source</span> that has an active bank connection, a confirmation dialog will warn you. Confirming deletes the source and automatically disconnects the bank sync. Previously synced transactions are also removed along with the source.</p>
    </Section>

    <Section title="Bank Feed — Carpco-OS Ledger">
      <p>If a client's financial source is linked to a Carpco-OS client record, two additional controls appear in the green strip:</p>
      <ul className="list-disc pl-4 space-y-1.5 mt-1">
        <li><span className="font-medium text-foreground">Link to carpco-os</span> — registers this Teller connection with the Carpco-OS system so it can sync transactions independently from there. Do this once per connection after the initial setup.</li>
        <li><span className="font-medium text-foreground">Push ledger</span> — manually pushes all cached transactions from Central Command into the Carpco-OS bank ledger. Used when backfilling historical data or after a reconnect.</li>
      </ul>
      <Note>These controls only appear when the financial source has a Carpco-OS client ID configured. Contact Wade if you need to set this up for a client.</Note>
    </Section>
  </div>
);

const NotificationsContent = () => (
  <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
    <Section title="The Notification Bell">
      <p>The bell icon in the top-right header shows a red badge count when you have unread notifications. Click it to open a quick popover showing your latest 10. Click <span className="font-medium text-foreground">View all</span> at the bottom to open the full Notifications page.</p>
    </Section>
    <Section title="What Triggers a Notification?">
      <ul className="list-disc pl-4 space-y-1">
        <li><span className="font-medium text-foreground">Task assigned</span> — someone added you as an assignee on a task</li>
        <li><span className="font-medium text-foreground">Task comment</span> — someone commented on a task you're assigned to</li>
        <li><span className="font-medium text-foreground">@Mention</span> — someone @mentioned you in a team chat or feedback comment</li>
        <li><span className="font-medium text-foreground">Status changed</span> — a project moved to a new workflow status</li>
        <li><span className="font-medium text-foreground">Added to client</span> — you were added as a team member on a client</li>
        <li><span className="font-medium text-foreground">Project completed</span> — a project you're assigned to was marked complete</li>
        <li><span className="font-medium text-foreground">Project comment</span> — someone posted in the team chat for a client you're on</li>
        <li><span className="font-medium text-foreground">Client message</span> — a portal client sent a message</li>
        <li><span className="font-medium text-foreground">Client file upload</span> — a portal client uploaded a document</li>
        <li><span className="font-medium text-foreground">Client task complete</span> — a portal client checked off a task</li>
      </ul>
    </Section>
    <Section title="The Read/Unread Checkbox">
      <p>Each notification row has a <span className="font-medium text-foreground">circle checkbox on the left</span>:</p>
      <ul className="list-disc pl-4 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Hollow circle</span> = unread. Click to mark it as read (turns solid green with a checkmark).</li>
        <li><span className="font-medium text-foreground">Green filled circle</span> = already read. Click again to mark it back to unread.</li>
      </ul>
      <p className="mt-2">Unread rows also show a blue left-border stripe so they're easy to spot at a glance.</p>
    </Section>
    <Section title="Navigating From a Notification">
      <p>Notification rows do <span className="font-medium text-foreground">not</span> navigate on click — you can read them without accidentally leaving the page. Instead, use the explicit links:</p>
      <ul className="list-disc pl-4 space-y-1 mt-1">
        <li>Click the <span className="font-medium text-foreground">colored client name pill</span> at the top of a row to go to that client's profile.</li>
        <li>Click <span className="font-medium text-foreground">View client</span> or <span className="font-medium text-foreground">View project</span> links below the notification body to navigate. Both also mark the notification as read.</li>
      </ul>
    </Section>
    <Section title="Filtering & Searching">
      <p>Use the <span className="font-medium text-foreground">category tabs</span> (All / Projects / Mentions / Client Activity) to focus on a specific type. Use the search bar to find notifications by client name, title, or message text. Use the <span className="font-medium text-foreground">All / Unread / Read</span> toggle to filter by read state.</p>
    </Section>
    <Section title="Deleting Notifications">
      <ul className="list-disc pl-4 space-y-1">
        <li>The <span className="font-medium text-foreground">trash icon</span> on each row permanently deletes that single notification.</li>
        <li><span className="font-medium text-foreground">Delete All Read (N)</span> in the header permanently deletes all your already-read notifications at once.</li>
        <li>Deleted notifications cannot be recovered.</li>
      </ul>
    </Section>
    <Section title="Email Notifications">
      <p>You can opt in to receive email copies of any notification type. Go to <span className="font-medium text-foreground">Settings → My Preferences → Notification Preferences</span> and toggle the Email column for each type you want. Emails are sent from noreply@carpentercpas.com via Resend.</p>
    </Section>
    <Section title="Auto-Refresh">
      <p>The bell popover and the Notifications page both refresh automatically every 30 seconds — no need to reload the page.</p>
    </Section>
  </div>
);

const InvoicingContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      Central Command's invoicing tools let you create invoices directly from time entries, track payment status, and generate PDF invoices to send to clients.
    </p>

    <Section title="Creating an Invoice from Time Entries">
      <ol className="list-decimal pl-5 space-y-2 mt-1">
        <li>Go to the <span className="font-medium text-foreground">Invoices</span> page in the sidebar.</li>
        <li>Click <span className="font-medium text-foreground">Create from Time Entries</span>.</li>
        <li>Select a <span className="font-medium text-foreground">client</span>, choose a <span className="font-medium text-foreground">date range</span>, and enter an <span className="font-medium text-foreground">hourly rate</span>. You can optionally add a tax percentage.</li>
        <li>Click <span className="font-medium text-foreground">Create Invoice</span>. A draft invoice is generated with one line item per billable time entry in that range.</li>
      </ol>
      <Tip>Only billable time entries are included. Non-billable entries are automatically excluded from the invoice.</Tip>
    </Section>

    <Section title="Invoice Statuses">
      <p>Invoices follow a simple lifecycle:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Draft</span> — invoice is being built. You can edit line items, dates, and rates freely.</li>
        <li><span className="font-medium text-foreground">Sent</span> — invoice has been sent to the client. Line item edits are restricted (admins can still edit).</li>
        <li><span className="font-medium text-foreground">Paid</span> — payment has been recorded against the invoice.</li>
      </ul>
    </Section>

    <Section title="Downloading the Invoice PDF">
      <p>Open any invoice and click the <span className="font-medium text-foreground">Download PDF</span> button. The PDF includes the firm's logo, address, phone, and website, along with all line items and totals. Send it to your client via email or upload it to their portal.</p>
    </Section>

    <Section title="Marking an Invoice Paid">
      <p>From the invoice list, click the <span className="font-medium text-foreground">$</span> quick-action button on any sent invoice row to mark it paid in one click. You can also open the invoice detail and use the status controls there.</p>
    </Section>

    <Section title="Invoice Auto-Numbering">
      <p>Invoices are automatically numbered in <span className="font-medium text-foreground">YY-NNN</span> format — for example, <span className="font-mono text-foreground">26-001</span>, <span className="font-mono text-foreground">26-002</span>, etc. The counter resets each calendar year.</p>
    </Section>

    <Section title="Admin: Editing Sent or Paid Invoices">
      <p>Admins can edit invoices in any status — not just drafts. The Edit button on the invoice detail page is visible to admins regardless of whether the invoice is Draft, Sent, or Paid.</p>
    </Section>

    <Section title="Admin: Reverting to Draft or Deleting">
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Revert to Draft</span> — admins can move a Sent invoice back to Draft, or a Paid invoice back to Sent, from the invoice detail page.</li>
        <li><span className="font-medium text-foreground">Delete</span> — admins can permanently delete non-draft invoices. Use with caution — this action cannot be undone.</li>
      </ul>
    </Section>

    <Section title="Status History">
      <p>The bottom of the invoice detail page shows a <span className="font-medium text-foreground">Status History</span> card. Every time the invoice status changes, an entry is logged with the timestamp and the user who made the change.</p>
    </Section>
  </div>
);

const ReportsContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      The Reports section gives managers and admins visibility into how the team's time is being tracked and how staff are utilizing their capacity.
    </p>

    <Note>Reports are available to Manager and Admin roles only. Contractors cannot access this section.</Note>

    <Section title="Time Utilization Report">
      <p>Located at <span className="font-medium text-foreground">Reports → Time Utilization</span>. This report shows how much time each staff member has logged over a selected date range.</p>
      <ul className="list-disc pl-5 space-y-1 mt-2">
        <li><span className="font-medium text-foreground">Date range</span> — use date presets (This Week, Last Week, This Month, etc.) or set a custom range.</li>
        <li><span className="font-medium text-foreground">Staff filter</span> — show all staff or focus on specific team members.</li>
        <li>The <span className="font-medium text-foreground">sortable table</span> shows total hours, billable hours, billable percentage, and entry count per person.</li>
        <li>A <span className="font-medium text-foreground">stacked bar chart</span> below the table visualizes billable vs. non-billable hours at a glance.</li>
        <li>Click <span className="font-medium text-foreground">Export CSV</span> to download the report data for use in Excel or other tools.</li>
      </ul>
    </Section>

    <Section title="Capacity Report">
      <p>Located at <span className="font-medium text-foreground">Reports → Capacity</span>. This report compares each staff member's logged hours against their weekly capacity target.</p>
      <ul className="list-disc pl-5 space-y-1 mt-2">
        <li>Use the <span className="font-medium text-foreground">week navigation</span> (This Week / Last Week / Prev / Next) to move between weeks.</li>
        <li>Each row shows the staff member's name, hours logged, their capacity target, and a <span className="font-medium text-foreground">color-coded status badge</span>:</li>
      </ul>
      <ul className="list-disc pl-5 space-y-1 mt-1 ml-4">
        <li><span className="font-medium text-green-700">Green</span> — on track (hours are at or near the weekly target).</li>
        <li><span className="font-medium text-amber-600">Amber</span> — under capacity (logged significantly fewer hours than target).</li>
        <li><span className="font-medium text-red-600">Red</span> — over capacity (logged more hours than the weekly target).</li>
      </ul>
      <Tip>A staff member's weekly capacity target is set in their user profile. Admins can update it via Settings → Admin → Users & Roles.</Tip>
    </Section>
  </div>
);

const SMSContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      Central Command supports two-way SMS messaging with clients, powered by Twilio. Staff can send and receive text messages directly from a client profile.
    </p>

    <Section title="Opting a Contact In">
      <p>SMS must be enabled per contact before you can send messages. On the client's profile page, find the contact row and check the <span className="font-medium text-foreground">SMS</span> checkbox. This records the contact's opt-in for SMS communication.</p>
      <Note>Only opt in contacts who have given consent to receive text messages. This is required for regulatory compliance.</Note>
    </Section>

    <Section title="Sending an SMS">
      <p>Once a contact is opted in, a <span className="font-medium text-foreground">Send SMS</span> button appears on their contact row. Click it to open the send dialog.</p>
      <p className="mt-2">You can also send directly from the SMS thread tab:</p>
      <ol className="list-decimal pl-5 space-y-1 mt-1">
        <li>Open the client's profile and click the <span className="font-medium text-foreground">SMS</span> tab.</li>
        <li>Type your message in the reply bar at the bottom of the thread.</li>
        <li>Press <span className="font-medium text-foreground">Enter</span> to send (or <span className="font-medium text-foreground">Shift+Enter</span> for a newline).</li>
      </ol>
    </Section>

    <Section title="The SMS Thread">
      <p>The SMS tab shows a full iMessage-style conversation thread:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Outbound messages</span> (sent by staff) appear on the right in blue.</li>
        <li><span className="font-medium text-foreground">Inbound messages</span> (received from the client) appear on the left in gray.</li>
      </ul>
      <p className="mt-2">The thread refreshes automatically every <span className="font-medium text-foreground">15 seconds</span> — no need to reload the page when waiting for a reply.</p>
    </Section>

    <Section title="Admin: Twilio Configuration">
      <p>SMS requires Twilio credentials to be configured on the server. Admins can check the status at <span className="font-medium text-foreground">Settings → Admin → SMS (Twilio)</span>. The panel shows which environment variables are set and allows sending a test SMS to verify the connection.</p>
      <p className="mt-2">Required environment variables: <span className="font-mono text-foreground">TWILIO_ACCOUNT_SID</span>, <span className="font-mono text-foreground">TWILIO_AUTH_TOKEN</span>, and <span className="font-mono text-foreground">TWILIO_FROM_NUMBER</span>.</p>
    </Section>
  </div>
);

const AdminConfigContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      The Admin Settings page consolidates all firm-level configuration in one place. Access it at <span className="font-medium text-foreground">Settings → Admin</span> (admin role required).
    </p>

    <Section title="Invite Team">
      <p>Send email invitations to new staff members directly from this section. Enter the invitee's email address and role, then click Invite. They'll receive a link to set up their account.</p>
    </Section>

    <Section title="Users & Roles">
      <p>View and manage all user accounts in the system. Edit a user's name, email, role, and active status. Admins can also delete users who should no longer have access.</p>
      <Note>You cannot change your own role — another admin must do it.</Note>
    </Section>

    <Section title="Activity Codes">
      <p>Activity codes are the categories staff choose when logging time (e.g., Tax Preparation, Review, Admin). Manage them here:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Add</span> — create new codes with a name, category, and default billable setting.</li>
        <li><span className="font-medium text-foreground">Edit</span> — rename or change the category/billable default of an existing code.</li>
        <li><span className="font-medium text-foreground">Activate/Deactivate</span> — inactive codes are hidden from the time entry form but preserved on all historical entries. Use this instead of deleting.</li>
      </ul>
    </Section>

    <Section title="Pay Periods">
      <p>Link to the Pay Periods management page where admins can open, close, and manage pay period status. See the <span className="font-medium text-foreground">Pay Periods</span> help topic for details on the semi-monthly schedule.</p>
    </Section>

    <Section title="Data Import">
      <p>Tools for importing data from Financial Cents and other sources. Includes client groups, assignees, relationships, and more. Link to the full Import page.</p>
    </Section>

    <Section title="Email Config">
      <p>Configure the Resend API key and sender email address used for all system-generated notification emails (task assignments, status changes, etc.).</p>
    </Section>

    <Section title="M365 Mailboxes">
      <p>Add or remove Microsoft 365 mailboxes that the system monitors for client email history. See the <span className="font-medium text-foreground">Emails</span> help topic for details on how personal and shared mailboxes work.</p>
    </Section>

    <Section title="SmartVault">
      <p>Configure SmartVault integration credentials. Once set up, staff can browse client folder trees directly from the client profile's SmartVault panel.</p>
    </Section>

    <Section title="SMS (Twilio)">
      <p>Shows the status of the three required Twilio environment variables and lets you send a test SMS to verify the connection is working. See the <span className="font-medium text-foreground">SMS Messaging</span> help topic for setup details.</p>
    </Section>
  </div>
);

const DailyReportContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      The Daily Time Report is an automated email that gives admins a quick read on time-tracking compliance across the team — no login required.
    </p>

    <Section title="What It Does">
      <p>Every weekday at <span className="font-medium text-foreground">7:00 AM Eastern</span>, the system emails all admin users a summary of the previous day's time tracking activity:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li>Staff members who <span className="font-medium text-foreground">logged time</span> the previous day, with total hours.</li>
        <li>Staff members who <span className="font-medium text-foreground">did not log any time</span> — flagged so you can follow up.</li>
      </ul>
    </Section>

    <Section title="Monday Coverage">
      <p>Monday's report covers <span className="font-medium text-foreground">Friday</span> — not Sunday or Saturday. Since the report only runs on weekdays, Monday's email bridges the weekend gap and shows who logged time on the last business day of the previous week.</p>
    </Section>

    <Section title="Manual Send">
      <p>Admins can trigger the report at any time without waiting for the scheduled send:</p>
      <ol className="list-decimal pl-5 space-y-1 mt-1">
        <li>Go to <span className="font-medium text-foreground">Settings → Admin</span>.</li>
        <li>Find the <span className="font-medium text-foreground">Daily Time Report</span> section.</li>
        <li>Click <span className="font-medium text-foreground">Send Now</span>.</li>
      </ol>
      <p className="mt-2">The manual send uses the same logic as the scheduled send — it covers the previous business day and sends to all admin users. Any errors are displayed inline in the UI.</p>
      <Tip>Use Send Now if the scheduled email didn't arrive (e.g., server was down at 7 AM) or if you want to check in mid-day.</Tip>
    </Section>

    <Section title="Who Receives the Report">
      <p>The daily report is sent to all users with the <span className="font-medium text-foreground">Admin</span> role only. Managers and staff do not receive it.</p>
    </Section>
  </div>
);

const CalendarContent = () => (
  <div>
    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
      The Calendar page shows your Outlook calendar in a read-only week view, pulled directly from Microsoft 365.
    </p>
    <Section title="Navigating the Calendar">
      <p>Use the <span className="font-medium text-foreground">← Prev</span> and <span className="font-medium text-foreground">Next →</span> buttons to move between weeks, or click <span className="font-medium text-foreground">This Week</span> to jump back to the current week. The calendar always shows Monday through Friday.</p>
    </Section>
    <Section title="Reading Event Cards">
      <p>Each event card shows the time range, subject, and location (if set). A colored dot indicates availability status:</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><span className="font-medium text-foreground">Blue dot</span> — Busy</li>
        <li><span className="font-medium text-foreground">Amber dot</span> — Tentative</li>
        <li><span className="font-medium text-foreground">Red dot</span> — Out of Office</li>
        <li><span className="font-medium text-foreground">Gray dot</span> — Free / Working Elsewhere</li>
      </ul>
      <p className="mt-2">All-day events appear at the top of each day column with a dashed border.</p>
    </Section>
    <Section title="Opening in Outlook">
      <p>Hover over any event card to reveal an external link icon — click it to open the event directly in Outlook Web.</p>
    </Section>
    <Note>The Calendar is read-only. To create, edit, or delete events, use Outlook or your Microsoft 365 calendar directly. Admin must grant <span className="font-mono">Calendars.Read</span> permission in Azure for this page to work.</Note>
  </div>
);

export const TOPICS: Topic[] = [
  {
    id: "clients",
    label: "Clients",
    icon: <Building2 className="w-4 h-4" />,
    content: <ClientsContent />,
  },
  {
    id: "client-groups",
    label: "Client Groups",
    icon: <Users2 className="w-4 h-4" />,
    content: <ClientGroupsContent />,
  },
  {
    id: "messages",
    label: "Messages",
    icon: <MessageSquare className="w-4 h-4" />,
    content: <MessagesContent />,
  },
  {
    id: "emails",
    label: "Emails",
    icon: <Inbox className="w-4 h-4" />,
    content: <EmailsContent />,
  },
  {
    id: "financial-sources",
    label: "Financial Sources",
    icon: <Landmark className="w-4 h-4" />,
    content: <FinancialSourcesContent />,
  },
  {
    id: "documents",
    label: "Client Documents",
    icon: <FolderOpen className="w-4 h-4" />,
    content: <DocumentsContent />,
  },
  {
    id: "invoicing",
    label: "Invoices & Billing",
    icon: <Receipt className="w-4 h-4" />,
    content: <InvoicingContent />,
  },
  {
    id: "reports",
    label: "Reports",
    icon: <BarChart2 className="w-4 h-4" />,
    content: <ReportsContent />,
  },
  {
    id: "sms",
    label: "SMS Messaging",
    icon: <MessageSquareText className="w-4 h-4" />,
    content: <SMSContent />,
  },
  {
    id: "admin-config",
    label: "Admin Settings",
    icon: <Settings2 className="w-4 h-4" />,
    content: <AdminConfigContent />,
  },
  {
    id: "daily-report",
    label: "Daily Time Report",
    icon: <CalendarClock className="w-4 h-4" />,
    content: <DailyReportContent />,
  },
  {
    id: "projects",
    label: "Projects",
    icon: <FolderOpen className="w-4 h-4" />,
    content: <ProjectsContent />,
  },
  {
    id: "workflow",
    label: "Workflow Pipelines",
    icon: <GitBranch className="w-4 h-4" />,
    content: <WorkflowContent />,
  },
  {
    id: "tags",
    label: "Tags",
    icon: <Tags className="w-4 h-4" />,
    content: <TagsContent />,
  },
  {
    id: "notifications",
    label: "Notifications",
    icon: <Bell className="w-4 h-4" />,
    content: <NotificationsContent />,
  },
  {
    id: "tasks",
    label: "Tasks",
    icon: <ListTodo className="w-4 h-4" />,
    content: <TasksContent />,
  },
  {
    id: "logging-time",
    label: "Logging Time",
    icon: <Clock className="w-4 h-4" />,
    content: <LoggingTimeContent />,
  },
  {
    id: "timesheet",
    label: "Your Timesheet",
    icon: <CalendarDays className="w-4 h-4" />,
    content: <TimesheetContent />,
  },
  {
    id: "submitting",
    label: "Submitting Time",
    icon: <Send className="w-4 h-4" />,
    content: <SubmittingContent />,
  },
  {
    id: "approvals",
    label: "Approvals",
    icon: <CheckSquare className="w-4 h-4" />,
    content: <ApprovalsContent />,
  },
  {
    id: "pto",
    label: "PTO & Holidays",
    icon: <Palmtree className="w-4 h-4" />,
    content: <PTOContent />,
  },
  {
    id: "pay-periods",
    label: "Pay Periods",
    icon: <CalendarClock className="w-4 h-4" />,
    content: <PayPeriodsContent />,
  },
  {
    id: "status-meanings",
    label: "Entry Statuses",
    icon: <Tag className="w-4 h-4" />,
    content: <StatusMeaningsContent />,
  },
  {
    id: "calendar",
    label: "Calendar",
    icon: <CalendarDays className="w-4 h-4" />,
    content: <CalendarContent />,
  },
];

export function HelpSheet({ open, onOpenChange }: HelpSheetProps) {
  const [activeId, setActiveId] = useState("logging-time");
  const contentRef = useRef<HTMLDivElement>(null);
  const [helpAutoDismiss] = useUserPref(PREF_HELP_AUTO_DISMISS, true);
  const [pinned, setPinned] = useState(false);

  const activeTopic = TOPICS.find((t) => t.id === activeId) ?? TOPICS[0];

  function selectTopic(id: string) {
    setActiveId(id);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }

  // Reset to first topic and apply pref when sheet opens
  useEffect(() => {
    if (open) {
      setActiveId("logging-time");
      setPinned(!helpAutoDismiss);
    }
  }, [open, helpAutoDismiss]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        className="sm:max-w-2xl w-full p-0 flex flex-col"
        onInteractOutside={(e) => { if (pinned) e.preventDefault(); }}
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b shrink-0 pr-12">
          <SheetTitle className="text-lg flex items-center gap-2">
            Help &amp; Guidance
            <button
              onClick={() => setPinned((v) => !v)}
              className={`p-1 rounded transition-colors ${
                pinned
                  ? "text-primary bg-primary/10 hover:bg-primary/20"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent"
              }`}
              title={pinned ? "Unpin — clicking outside will close" : "Pin — keep open when clicking outside"}
            >
              {pinned ? <Pin className="w-3.5 h-3.5" /> : <PinOff className="w-3.5 h-3.5" />}
            </button>
          </SheetTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            Time tracking, timesheets, approvals, and more
          </p>
        </SheetHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Left nav */}
          <nav className="w-44 shrink-0 border-r bg-muted/30 flex flex-col gap-0.5 p-2 overflow-y-auto">
            {TOPICS.map((topic) => (
              <button
                key={topic.id}
                onClick={() => selectTopic(topic.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors w-full ${
                  activeId === topic.id
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                }`}
              >
                {topic.icon}
                <span className="leading-tight">{topic.label}</span>
              </button>
            ))}
          </nav>

          {/* Content area */}
          <div ref={contentRef} className="flex-1 overflow-y-auto px-5 py-4">
            <h2 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
              {activeTopic.icon}
              {activeTopic.label}
            </h2>
            {activeTopic.content}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
